import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// import { Button } from '@mui/material';

import { shortName } from 'utils/names';
import { IconFlight } from 'assets/icons';
import { getCity, getRubles } from 'utils/helpers';
import { getMomentDate, getMomentTime } from 'utils/moment';
import { changeOrderStateForCyrillic } from 'pages/Orders/helpers';
import MText from 'components/MaterialUI/MText';
import FormInputDisplay from 'components/FormElements/FormInputDisplay';
import OrderWays from 'pages/Orders/AviaOrders/AviaOrderWays/OrderWays';
import OrderPassengers from 'pages/Orders/AviaOrders/AviaOrderPassengers/OrderPassengers';
import { RegularWrapper, Text, Divider } from 'components/common';
import { CardWrapper, SubtitleItemWithoutDot, SubtitleItemWrap, SubtitleWrap, Icon } from './AviaOrdersListCard.styles';
import MTooltip from 'components/MaterialUI/MTooltip';
import MToggleIconButton from 'components/MaterialUI/MToggleIconButton';
// import DocumentOptions from './AviaOrderPassengers/DocumentOptions';

const AviaOrdersListCard = (props) => {
  const {
    isLoading,
    order,
    order: { ['order_items']: orderItems = [] },
  } = props;

  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const { originalPrice, totalOrderAmount } = orderItems.reduce(
    (amount, { reservation }) => {
      const totalOrderAmount = reservation?.actual_sell_price?.order_item_total?.gross?.cents * orderItems?.length || 0;
      const originalPrice = reservation?.initial_sell_price?.provider_total?.gross?.cents * orderItems?.length || 0;

      return {
        totalOrderAmount,
        originalPrice,
      };
    },
    {
      totalOrderAmount: 0,
      originalPrice: 0,
    },
  );

  const isAllPaid = orderItems.every(({ payment_intent }) => payment_intent?.state === 'paid');

  const getNames = () => {
    return orderItems.map(({ traveller }) => traveller && shortName(traveller)).join(', ');
  };

  const timeExpiresIn = (time) => {
    return `${getMomentDate(time)} ${getMomentTime(time)}`;
  };

  const getDepartureTime = (segment, nameAirp, dateDep) => {
    return `${getMomentDate(segment[dateDep], segment[nameAirp]?.['time_zone'])} в ${getMomentTime(
      segment[dateDep],
      segment[nameAirp]?.['time_zone'],
    )}`;
  };

  const createAndUpdateTime = (date) => {
    return `${getMomentDate(date)} в ${getMomentTime(date)}`;
  };

  const getSubtitleWithTransfers = (segments) => {
    const subtitles = segments.reduce((airCompanyNames, segment) => {
      airCompanyNames.push({
        name: segment?.air_company?.name,
        code: `${segment?.air_company?.code} ${segment?.flight_number}`,
      });

      return airCompanyNames;
    }, []);

    return (
      <SubtitleWrap wrap>
        <SubtitleItemWrap>{t('Plurals.Transfers', { count: segments?.length - 1 })}</SubtitleItemWrap>
        {subtitles.map((subtitle) => (
          <MTooltip placement="top" title={subtitle.name}>
            <SubtitleItemWithoutDot>{`${subtitle.code} ${subtitle.name}`.slice(0, 21) + '...'}</SubtitleItemWithoutDot>
          </MTooltip>
        ))}
      </SubtitleWrap>
    );
  };

  const getTransitionsSubtitle = (segments) => {
    return segments.length === 1 ? (
      <SubtitleWrap wrap>
        <SubtitleItemWrap>{'Прямой'}</SubtitleItemWrap>
        {segments.map((segment, index) => (
          <>
            <SubtitleItemWrap
              right
              key={index}
            >{`${segment?.air_company?.code} ${segment?.flight_number}`}</SubtitleItemWrap>
            <SubtitleItemWrap wrap right key={segment?.air_company?.name}>
              {segment?.air_company?.name}
            </SubtitleItemWrap>
          </>
        ))}
      </SubtitleWrap>
    ) : (
      getSubtitleWithTransfers(segments)
    );
  };

  const renderWays = (nameAirp, dateDep) => {
    const list = orderItems[0]?.['reservation']?.['direction']?.['legs'] || [{ id: 1 }];
    return list.map((leg) => {
      const { segments = [] } = leg;

      return segments.map((segment) => {
        return (
          <RegularWrapper>
            {nameAirp === 'dep_airp' && (
              <img
                alt={segment['air_company']}
                style={{ height: 16, margin: '10px 10px 0 0' }}
                src={`https://f.worldota.net/avia/master/6fced832/public/suppliers/${segment['air_company']?.['code']}.png`}
              />
            )}
            <RegularWrapper margin={nameAirp === 'arr_airp' ? '0 0 47px 0' : null} flow={'column'}>
              <Text color={'#3D3F43'} size={'14px'} fontWeight={'500'} children={getCity(segment[nameAirp])} />
              {getDepartureTime(segment, nameAirp, dateDep)}
              {nameAirp === 'dep_airp' && getTransitionsSubtitle(segments)}
            </RegularWrapper>
          </RegularWrapper>
        );
      });
    });
  };

  return (
    <CardWrapper flow={'column'} open={open}>
      <RegularWrapper align="center" justify="space-between">
        <RegularWrapper align="center" justify="flex-start">
          <Icon>
            <IconFlight />
          </Icon>
          <RegularWrapper width={'160px'} flow={'column'}>
            <MText variant="subtitle1" color="textSecondary" children={`${t('Orders.Order')}:`} />
            <MText {...{ isLoading }} width={130} variant="h3" weight="bold" children={order['code']} />
            <MText
              {...{ isLoading }}
              width={70}
              variant="subtitle2"
              sx={{ color: 'info.main' }}
              children={changeOrderStateForCyrillic(order['state'])}
            />
          </RegularWrapper>

          <RegularWrapper justify={'space-around'} width={'450px'}>
            <FormInputDisplay
              {...{ isLoading }}
              label={t('Orders.CreatedAt')}
              value={createAndUpdateTime(order['created_at'])}
            />
            <FormInputDisplay
              {...{ isLoading }}
              label={t('Orders.ChangedAt')}
              value={createAndUpdateTime(order['updated_at'])}
            />
            <MTooltip placement="top" title={order['owner']?.['email']}>
              <div>
                <FormInputDisplay
                  {...{ isLoading }}
                  label={t('Users.User')}
                  value={order['owner']?.['email'].slice(0, 15) + '...'}
                />
              </div>
            </MTooltip>
          </RegularWrapper>
        </RegularWrapper>

        <RegularWrapper align={'center'} justify={'flex-end'}>
          {/*TODO кнопки выписать всем отменить всем*/}
          {/*<Button*/}
          {/*  style={{*/}
          {/*    marginRight: 7,*/}
          {/*    background: '#F2F4F6',*/}
          {/*    color: '#71757F',*/}
          {/*    border: '1px solid #F2F4F6',*/}
          {/*  }}*/}
          {/*  size="small"*/}
          {/*  variant="outlined"*/}
          {/*  disabled={false}*/}
          {/*  onClick={() => {}}*/}
          {/*  children={'Отменить всем'}*/}
          {/*/>*/}
          {/*<Button*/}
          {/*  size="small"*/}
          {/*  style={{*/}
          {/*    background: '#EBF7F6',*/}
          {/*    color: '#35A9A2',*/}
          {/*    border: '1px solid #EBF7F6',*/}
          {/*  }}*/}
          {/*  variant="contained"*/}
          {/*  onClick={() => {}}*/}
          {/*  disabled={false}*/}
          {/*  children={'Выписать всем'}*/}
          {/*/>*/}
          {/*<DocumentOptions passenger={orderItems[0]} />*/}
          <MToggleIconButton {...{ open, setOpen }} />
        </RegularWrapper>
      </RegularWrapper>

      <Divider height="2px" bgColor={'#F2F4F6'} margin={'15px 0 20px 0'} />

      <RegularWrapper justify={'space-between'} margin={'20px 0 0 0'}>
        <RegularWrapper width={'230px'}>
          <FormInputDisplay
            margin={'0 0 0 58px'}
            {...{ isLoading }}
            label={t('Orders.From')}
            value={renderWays('dep_airp', 'dep_date')}
          />
        </RegularWrapper>

        <RegularWrapper width={'160px'}>
          <FormInputDisplay
            margin={'0 0 0 0'}
            {...{ isLoading }}
            label={t('Orders.To')}
            value={renderWays('arr_airp', 'arr_date')}
          />
        </RegularWrapper>

        <RegularWrapper width={'120px'} flow={'column'}>
          <FormInputDisplay {...{ isLoading }} label={t('Orders.Passengers')} value={getNames()} />
        </RegularWrapper>

        <RegularWrapper width={'120px'} flow={'column'}>
          <FormInputDisplay
            {...{ isLoading }}
            label={t('Orders.ContractDate')}
            value={order['contract']?.['begin_date']}
          />
          <FormInputDisplay
            margin={'5px 0 0 0'}
            {...{ isLoading }}
            label={t('Orders.ContractNumber')}
            value={order['contract']?.['number']}
          />
          <FormInputDisplay
            {...{ isLoading }}
            margin={'5px 0 0 0'}
            label={t('Orders.InformationAccount')}
            value={'-'}
          />
        </RegularWrapper>

        <RegularWrapper width={'120px'} flow={'column'}>
          <FormInputDisplay {...{ isLoading }} label={t('Orders.OriginalPrice')} value={getRubles(originalPrice)} />
          <FormInputDisplay
            margin={'5px 0 0 0'}
            {...{ isLoading }}
            label={t('Orders.RemainingPay')}
            value={getRubles(totalOrderAmount)}
          />
        </RegularWrapper>

        <RegularWrapper width={'120px'} flow={'column'}>
          <FormInputDisplay {...{ isLoading }} label={t('Orders.BackSum')} value={'-'} />
          <FormInputDisplay
            margin={'5px 0 0 0'}
            {...{ isLoading }}
            label={t('Orders.Payed')}
            value={isAllPaid ? 'Да' : 'Нет'}
          />
        </RegularWrapper>

        <RegularWrapper width={'120px'} flow={'column'}>
          <FormInputDisplay {...{ isLoading }} label={'К оплате'} value={getRubles(totalOrderAmount)} />
          <FormInputDisplay
            {...{ isLoading }}
            margin={'5px 0 0 0'}
            label={t('Orders.PayOutBefore')}
            value={timeExpiresIn(orderItems[0]?.['reservation']?.['expires_in'])}
          />
        </RegularWrapper>
      </RegularWrapper>

      {open && (
        <RegularWrapper flow={'column'}>
          <Divider height="2px" bgColor={'#F2F4F6'} margin={'24px 0 24px 12px'} />
          <OrderWays {...{ orderItems }} orderId={order['id']} />
          <Divider height="2px" bgColor={'#F2F4F6'} margin={'24px 0'} />
          <OrderPassengers
            {...{ orderItems }}
            orderId={order['id']}
            contractId={order?.['contract']?.['id']}
            ownerId={order?.owner?.id}
          />
        </RegularWrapper>
      )}
    </CardWrapper>
  );
};

AviaOrdersListCard.propTypes = {
  order: PropTypes.object,
  isLoading: PropTypes.bool,
};

AviaOrdersListCard.defaultProps = {
  order: {},
};

export default AviaOrdersListCard;
